import i18n from '@/app/i18n/i18n';

import type { SnackbarOptions, SnackbarType } from '@/context/SnackbarContext';

export type ServiceSnackbar = (
    options: SnackbarOptions | string,
    type?: Exclude<SnackbarType, 'open'>,
) => React.Key;

type ServiceRequest<Result> = {
    unwrap: () => Promise<Result>;
    unsubscribe?: () => void;
    reset?: () => void;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

/**
 * Preserve the imperative service contract at the RTK Query boundary.
 * Endpoint definitions own transport/caching; this only handles legacy toasts
 * and releases imperative subscriptions/results after the caller consumes them.
 */
export async function executeServiceRequest<Result>(
    request: ServiceRequest<Result>,
    showSnackbar?: ServiceSnackbar,
    url = '',
): Promise<Result> {
    try {
        return await request.unwrap();
    } catch (error: unknown) {
        const responseData = isRecord(error) ? error.data : undefined;
        const apiError = isRecord(responseData) ? responseData.error : undefined;
        const hasMessage = isRecord(apiError) && typeof apiError.msg === 'string';
        const translateParam = (value: unknown): unknown => {
            if (typeof value !== 'string') return value;
            const translated = i18n.t(value);
            return typeof translated === 'string' ? translated : value;
        };
        const interpolations =
            hasMessage && Array.isArray(apiError.msgParams)
                ? Object.fromEntries(
                      apiError.msgParams.map((value: unknown, index: number) => [
                          index,
                          translateParam(value),
                      ]),
                  )
                : {};
        const message = hasMessage
            ? String(i18n.t(apiError.msg as string, interpolations))
            : String(i18n.t('common.toast.error.connection_error'));
        showSnackbar?.(message, 'error');
        // Keep Error identity and the status/data/url fields callers already use.
        throw Object.assign(new Error(message), {
            name: 'ApiError',
            status:
                hasMessage && isRecord(error) && typeof error.status === 'number'
                    ? error.status
                    : 500,
            data: hasMessage ? apiError : error,
            url,
        });
    } finally {
        request.unsubscribe?.();
        request.reset?.();
    }
}

/** Same URL joining semantics as the former transport's baseURL override. */
export const serviceUrl = (url: string, baseUrl?: string): string =>
    !baseUrl || /^(?:[a-z][a-z\d+.-]*:)?\/\//i.test(url)
        ? url
        : `${baseUrl.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`;
